/**
 * Filter reconciliation helpers for the Performance tab.
 *
 * The Performance Filters modal lets the user pick a (concurrency,
 * length, cloud × hardware × accelerator × instance) combination, but
 * the seed catalog (`fetchPerformanceMetricsOptions`) only contains
 * combinations that actually exist in `performance_metrics`. The
 * helpers below take a current filters object and return one whose
 * fields are guaranteed to exist in the catalog — preferring the
 * caller's existing values when they're already valid, and falling
 * back to the first seeded value otherwise.
 *
 * Used by:
 *   - `PerformanceFiltersModal` (re-reconciles environments after the
 *     user changes cloud / hardware / accelerator).
 *   - `PerformanceTab` (auto-reconciles default filters against the
 *     catalog on baseModel change so the page never lands on a
 *     "No data" combination when at least one seeded combo exists).
 */

/**
 * Build a valid environment from the given catalog — picks the first
 * (cloud, hardware, accelerator, instance_type) combo that the catalog
 * actually contains so the new row always points at real data.
 *
 * @param {Object} catalog
 * @returns {{cloudProvider: string, hardwareDevice: string, accelerator: ?string, instanceType: string}}
 */
export function firstCatalogEnvironment(catalog) {
  const cloudProvider = Object.keys(catalog || {})[0];
  if (!cloudProvider) {
    return { cloudProvider: '', hardwareDevice: '', accelerator: null, instanceType: '' };
  }
  const hardwareDevice = Object.keys(catalog[cloudProvider] || {})[0] || '';
  const accelerators = Object.keys(catalog[cloudProvider]?.[hardwareDevice] || {});
  const acceleratorKey = accelerators[0] || 'null';
  const accelerator = acceleratorKey === 'null' ? null : acceleratorKey;
  const instanceType =
    catalog[cloudProvider]?.[hardwareDevice]?.[acceleratorKey]?.[0]?.value || '';
  return { cloudProvider, hardwareDevice, accelerator, instanceType };
}

// -----------------------------------------------------------------------------
// Duplicate-aware availability filtering
//
// Two environments are redundant when they resolve to the same
// (cloud, hardware, accelerator, instance) tuple — the comparison would
// show identical metrics. Rather than letting the user pick a duplicate and
// warning afterwards, the modal hides any option that would lead exclusively
// to a tuple already chosen by another environment. The helpers below answer
// "which clouds / hardware / accelerators / instances still have at least one
// unused tuple?" given the other environments already configured.
// -----------------------------------------------------------------------------

const TUPLE_SEP = '\u0000';

function acceleratorKeyOf(accelerator) {
  return accelerator == null ? 'null' : accelerator;
}

function tupleKey(cloudProvider, hardwareDevice, acceleratorKey, instanceType) {
  return [cloudProvider, hardwareDevice, acceleratorKey, instanceType].join(TUPLE_SEP);
}

/** Build the set of tuple keys already taken by `otherEnvs`. */
function takenTupleKeys(otherEnvs) {
  const taken = new Set();
  (Array.isArray(otherEnvs) ? otherEnvs : []).forEach((env) => {
    if (!env) return;
    taken.add(
      tupleKey(
        env.cloudProvider,
        env.hardwareDevice,
        acceleratorKeyOf(env.accelerator),
        env.instanceType
      )
    );
  });
  return taken;
}

function instanceValuesFor(catalog, cloudProvider, hardwareDevice, acceleratorKey) {
  return (catalog?.[cloudProvider]?.[hardwareDevice]?.[acceleratorKey] || []).map(
    (i) => i.value
  );
}

function freeInstanceValues(catalog, cloudProvider, hardwareDevice, acceleratorKey, taken) {
  return instanceValuesFor(catalog, cloudProvider, hardwareDevice, acceleratorKey).filter(
    (value) => !taken.has(tupleKey(cloudProvider, hardwareDevice, acceleratorKey, value))
  );
}

function freeAcceleratorKeys(catalog, cloudProvider, hardwareDevice, taken) {
  return Object.keys(catalog?.[cloudProvider]?.[hardwareDevice] || {}).filter(
    (acceleratorKey) =>
      freeInstanceValues(catalog, cloudProvider, hardwareDevice, acceleratorKey, taken).length > 0
  );
}

function freeHardwareDevices(catalog, cloudProvider, taken) {
  return Object.keys(catalog?.[cloudProvider] || {}).filter(
    (hardwareDevice) => freeAcceleratorKeys(catalog, cloudProvider, hardwareDevice, taken).length > 0
  );
}

function freeCloudProviders(catalog, taken) {
  return Object.keys(catalog || {}).filter(
    (cloudProvider) => freeHardwareDevices(catalog, cloudProvider, taken).length > 0
  );
}

/** Cloud providers that still have at least one tuple not taken by `otherEnvs`. */
export function availableCloudProviders(catalog, otherEnvs) {
  return freeCloudProviders(catalog, takenTupleKeys(otherEnvs));
}

/** Hardware devices (for `cloudProvider`) that still have an unused tuple. */
export function availableHardwareDevices(catalog, cloudProvider, otherEnvs) {
  return freeHardwareDevices(catalog, cloudProvider, takenTupleKeys(otherEnvs));
}

/** Accelerator options (for cloud/hardware) that still have an unused instance. */
export function availableAcceleratorOptions(catalog, cloudProvider, hardwareDevice, otherEnvs) {
  return freeAcceleratorKeys(
    catalog,
    cloudProvider,
    hardwareDevice,
    takenTupleKeys(otherEnvs)
  ).map((acceleratorKey) => ({
    value: acceleratorKey === 'null' ? null : acceleratorKey,
    label: acceleratorKey === 'null' ? '(none)' : acceleratorKey,
  }));
}

/** Instance-type options (for cloud/hardware/accelerator) not taken by `otherEnvs`. */
export function availableInstanceTypeOptions(
  catalog,
  cloudProvider,
  hardwareDevice,
  accelerator,
  otherEnvs
) {
  const acceleratorKey = acceleratorKeyOf(accelerator);
  const taken = takenTupleKeys(otherEnvs);
  return (catalog?.[cloudProvider]?.[hardwareDevice]?.[acceleratorKey] || []).filter(
    (option) => !taken.has(tupleKey(cloudProvider, hardwareDevice, acceleratorKey, option.value))
  );
}

/**
 * Resolve `env` to a valid, non-duplicate catalog combination. Prefers each
 * field the caller supplied when it still has an unused tuple beneath it,
 * otherwise falls back to the first option that does. `otherEnvs` are the
 * sibling environments whose tuples must be avoided. Used both when adding a
 * new environment (pass `{}` to get the first free combo) and when the user
 * changes an upstream field.
 *
 * @param {{cloudProvider?: string, hardwareDevice?: string, accelerator?: ?string, instanceType?: string}} env
 * @param {Object} catalog
 * @param {Array<Object>} otherEnvs
 */
export function resolveAvailableEnvironment(env, catalog, otherEnvs) {
  if (!catalog || !Object.keys(catalog).length) return env;
  const taken = takenTupleKeys(otherEnvs);

  const allClouds = Object.keys(catalog);
  const freeClouds = freeCloudProviders(catalog, taken);
  const cloudProvider =
    env?.cloudProvider && freeClouds.includes(env.cloudProvider)
      ? env.cloudProvider
      : freeClouds[0] || allClouds[0];

  const allHardware = Object.keys(catalog[cloudProvider] || {});
  const freeHardware = freeHardwareDevices(catalog, cloudProvider, taken);
  const hardwareDevice =
    env?.hardwareDevice && freeHardware.includes(env.hardwareDevice)
      ? env.hardwareDevice
      : freeHardware[0] || allHardware[0] || '';

  const allAccelerators = Object.keys(catalog[cloudProvider]?.[hardwareDevice] || {});
  const freeAccelerators = freeAcceleratorKeys(catalog, cloudProvider, hardwareDevice, taken);
  const preferredAcceleratorKey = acceleratorKeyOf(env?.accelerator);
  const acceleratorKey = freeAccelerators.includes(preferredAcceleratorKey)
    ? preferredAcceleratorKey
    : freeAccelerators[0] || allAccelerators[0] || 'null';
  const accelerator = acceleratorKey === 'null' ? null : acceleratorKey;

  const allInstances = instanceValuesFor(catalog, cloudProvider, hardwareDevice, acceleratorKey);
  const freeInstances = freeInstanceValues(catalog, cloudProvider, hardwareDevice, acceleratorKey, taken);
  const instanceType =
    env?.instanceType && freeInstances.includes(env.instanceType)
      ? env.instanceType
      : freeInstances[0] || allInstances[0] || '';

  return { cloudProvider, hardwareDevice, accelerator, instanceType };
}

/**
 * Keep only the parts of `env` that the catalog actually offers; rebuild
 * any field that no longer matches a seeded combo. Safe to call after a
 * cloud / hardware / accelerator change in the modal.
 *
 * @param {{cloudProvider?: string, hardwareDevice?: string, accelerator?: ?string, instanceType?: string}} env
 * @param {Object} catalog
 */
export function reconcileEnvWithCatalog(env, catalog) {
  if (!catalog || !Object.keys(catalog).length) return env;
  const cloudProvider = catalog[env?.cloudProvider]
    ? env.cloudProvider
    : Object.keys(catalog)[0];
  const hardwareMap = catalog[cloudProvider] || {};
  const hardwareDevice = hardwareMap[env?.hardwareDevice]
    ? env.hardwareDevice
    : Object.keys(hardwareMap)[0] || '';
  const acceleratorMap = hardwareMap[hardwareDevice] || {};
  const acceleratorKey = env?.accelerator == null ? 'null' : env.accelerator;
  const resolvedAcceleratorKey = acceleratorMap[acceleratorKey]
    ? acceleratorKey
    : Object.keys(acceleratorMap)[0] || 'null';
  const accelerator = resolvedAcceleratorKey === 'null' ? null : resolvedAcceleratorKey;
  const instanceList = acceleratorMap[resolvedAcceleratorKey] || [];
  const instanceType =
    instanceList.find((i) => i.value === env?.instanceType)?.value
    || instanceList[0]?.value
    || '';
  return { cloudProvider, hardwareDevice, accelerator, instanceType };
}

function envsEqual(a, b) {
  if (!a || !b) return a === b;
  return (
    a.cloudProvider === b.cloudProvider
    && a.hardwareDevice === b.hardwareDevice
    && (a.accelerator ?? null) === (b.accelerator ?? null)
    && a.instanceType === b.instanceType
  );
}

/**
 * Shallow comparison of two filters objects (concurrency, lengthCategory,
 * environments). Used to decide whether reconciliation produced a real
 * change worth pushing into state.
 */
export function filtersEqual(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.concurrency !== b.concurrency) return false;
  if (a.lengthCategory !== b.lengthCategory) return false;
  const ea = a.environments || [];
  const eb = b.environments || [];
  if (ea.length !== eb.length) return false;
  for (let i = 0; i < ea.length; i += 1) {
    if (!envsEqual(ea[i], eb[i])) return false;
  }
  return true;
}

/**
 * Reconcile a full filters object against an options catalog so every
 * dimension is guaranteed to be present in the seeded data:
 *   - `concurrency` is left as-is when it appears in `concurrencyLevels`,
 *     otherwise replaced with the first seeded level.
 *   - `lengthCategory` is left as-is when seeded, otherwise replaced
 *     with the first seeded length.
 *   - Each entry in `environments` is reconciled via
 *     `reconcileEnvWithCatalog`.
 *
 * Returns the original `filters` reference unchanged when the catalog
 * is empty, when there are no environments, or when every field is
 * already valid (so React state updates can short-circuit).
 *
 * @param {import('./performanceMetricsApi').PerformanceFilters} filters
 * @param {{concurrencyLevels?: number[], lengthCategories?: string[], machineCatalog?: Object}} options
 * @param {{preferredCloudProvider?: string}} [prefs]
 * @returns {import('./performanceMetricsApi').PerformanceFilters}
 */
export function reconcileFiltersWithCatalog(filters, options, prefs = {}) {
  if (!filters) return filters;
  const concurrencyLevels = Array.isArray(options?.concurrencyLevels)
    ? options.concurrencyLevels
    : [];
  const lengthCategories = Array.isArray(options?.lengthCategories)
    ? options.lengthCategories
    : [];
  const catalog = options?.machineCatalog || {};

  // Nothing seeded for this base model -> leave filters untouched so the
  // user-facing "No data" state still surfaces (we'd be guessing values
  // that have no backing documents either).
  if (
    !concurrencyLevels.length
    && !lengthCategories.length
    && !Object.keys(catalog).length
  ) {
    return filters;
  }

  const concurrency = concurrencyLevels.length
    ? (concurrencyLevels.includes(filters.concurrency)
      ? filters.concurrency
      : concurrencyLevels[0])
    : filters.concurrency;

  const lengthCategory = lengthCategories.length
    ? (lengthCategories.includes(filters.lengthCategory)
      ? filters.lengthCategory
      : lengthCategories[0])
    : filters.lengthCategory;

  const preferredCloud = prefs.preferredCloudProvider;
  const environments = (filters.environments || []).map((env, index) => {
    const seeded =
      index === 0 && preferredCloud && catalog[preferredCloud]
        ? { ...env, cloudProvider: preferredCloud }
        : env;
    return reconcileEnvWithCatalog(seeded, catalog);
  });

  const next = { ...filters, concurrency, lengthCategory, environments };
  return filtersEqual(filters, next) ? filters : next;
}
